import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MessageCircle, FileText, Globe, Bot, User,
         Play, ChevronDown, ChevronUp, Plus, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// El historial de una ficha: todo lo que ha pasado con esa persona, venga del
// canal que venga. Es el mismo componente para vendedores, compradores,
// contactos y propiedades — solo cambia por qué campo se filtra.
//
// Las llamadas traen transcripción, resumen y grabación. Se enseña el resumen y
// la transcripción queda plegada: en una llamada de diez minutos son tres mil
// palabras que nadie va a leer de corrido, pero que hacen falta cuando alguien
// discute qué se dijo exactamente.

const CANAL = {
  llamada:    { icono: Phone,         color: '#c9a84c', etiqueta: 'Llamada' },
  email:      { icono: Mail,          color: '#60a5fa', etiqueta: 'Correo' },
  whatsapp:   { icono: MessageCircle, color: '#25D366', etiqueta: 'WhatsApp' },
  idealista:  { icono: Globe,         color: '#06b6d4', etiqueta: 'Idealista' },
  nota:       { icono: FileText,      color: '#94a3b8', etiqueta: 'Nota' },
  formulario: { icono: FileText,      color: '#a78bfa', etiqueta: 'Formulario' },
  app:        { icono: Globe,         color: '#a78bfa', etiqueta: 'App' },
  sistema:    { icono: Bot,           color: '#64748b', etiqueta: 'Sistema' },
};

const cuando = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const min = Math.round((Date.now() - d) / 60000);
  if (min < 1) return 'ahora mismo';
  if (min < 60) return `hace ${min} min`;
  if (min < 1440) return `hace ${Math.round(min / 60)} h`;
  if (min < 10080) return `hace ${Math.round(min / 1440)} días`;
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
};

const duracion = (s) => {
  if (!s && s !== 0) return null;
  const m = Math.floor(s / 60), r = s % 60;
  return m > 0 ? `${m} min ${r}s` : `${r}s`;
};

export function HistorialActividad({ orgId, sellerId, buyerId, contactId, propertyId, compacto }) {
  const [lineas, setLineas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [abierta, setAbierta] = useState(null);
  const [nota, setNota] = useState('');
  const [escribiendo, setEscribiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const hayFicha = sellerId || buyerId || contactId || propertyId;

  const cargar = useCallback(async () => {
    if (!hayFicha) { setCargando(false); return; }
    try {
      const q = new URLSearchParams();
      if (orgId) q.set('org_id', orgId);
      if (sellerId) q.set('seller_id', sellerId);
      if (buyerId) q.set('buyer_id', buyerId);
      if (contactId) q.set('contact_id', contactId);
      if (propertyId) q.set('property_id', propertyId);
      q.set('limit', compacto ? '8' : '60');
      const { data } = await axios.get(`${API}/crm/activity?${q}`);
      setLineas(Array.isArray(data) ? data : []);
    } catch (e) {
      // Sin historial no se rompe la ficha: simplemente no hay nada que enseñar.
      setLineas([]);
    } finally { setCargando(false); }
  }, [orgId, sellerId, buyerId, contactId, propertyId, compacto, hayFicha]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarNota = async () => {
    if (!nota.trim()) return;
    setGuardando(true);
    try {
      await axios.post(`${API}/crm/activity`, {
        channel: 'nota', org_id: orgId, body: nota,
        seller_id: sellerId || null, buyer_id: buyerId || null,
        contact_id: contactId || null, property_id: propertyId || null,
      });
      setNota(''); setEscribiendo(false);
      await cargar();
      toast.success('Nota guardada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar la nota');
    } finally { setGuardando(false); }
  };

  if (!hayFicha) return null;

  return (
    <div className="mt-6 p-6 bg-white/5 rounded-xl border border-white/10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-bold flex items-center gap-2">
          <Clock size={19} />
          Historial de actividad
          {lineas.length > 0 && (
            <span className="text-xs font-normal text-white/40">({lineas.length})</span>
          )}
        </h3>
        <button onClick={() => setEscribiendo(!escribiendo)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-sm transition">
          <Plus size={14} /> Nota
        </button>
      </div>

      {escribiendo && (
        <div className="mb-4">
          <textarea value={nota} onChange={(e) => setNota(e.target.value)}
            placeholder="Lo que quieras dejar apuntado sobre esta ficha…"
            className="w-full bg-gray-800 text-white p-3 rounded-lg border border-white/20 focus:border-yellow-600 focus:outline-none min-h-[80px] text-sm" />
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => { setEscribiendo(false); setNota(''); }}
              className="px-3 py-1.5 rounded-lg text-white/50 hover:text-white/80 text-sm transition">
              Cancelar
            </button>
            <button onClick={guardarNota} disabled={guardando || !nota.trim()}
              className="px-4 py-1.5 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-black font-semibold text-sm transition disabled:opacity-40">
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      )}

      {cargando ? (
        <p className="text-white/30 text-sm italic">Cargando…</p>
      ) : lineas.length === 0 ? (
        <p className="text-white/40 italic text-sm">
          Todavía no hay nada. Las llamadas, correos y mensajes aparecerán aquí solos.
        </p>
      ) : (
        <div className="space-y-2">
          {lineas.map((l) => {
            const c = CANAL[l.channel] || CANAL.sistema;
            const Icono = c.icono;
            const desplegada = abierta === l.activity_id;
            const hayMas = l.transcript || l.media_url;
            return (
              <div key={l.activity_id}
                className="relative pl-8 pb-2 border-l border-white/10 last:border-l-transparent">
                <div className="absolute left-0 top-0 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center"
                     style={{ background: '#111827', border: `1px solid ${c.color}40` }}>
                  <Icono size={12} style={{ color: c.color }} />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold" style={{ color: c.color }}>{c.etiqueta}</span>
                  {l.direction && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded"
                          style={{ background: 'rgba(255,255,255,0.06)', color: '#94a3b8' }}>
                      {l.direction === 'in' || l.direction === 'entrante' ? 'entrante'
                        : l.direction === 'out' || l.direction === 'saliente' ? 'saliente' : l.direction}
                    </span>
                  )}
                  {l.duration_seconds != null && (
                    <span className="text-[10px] text-white/40">{duracion(l.duration_seconds)}</span>
                  )}
                  <span className="text-[10px] text-white/30 ml-auto">{cuando(l.occurred_at)}</span>
                </div>

                {l.actor_name && (
                  <p className="text-[11px] text-white/40 mt-0.5 flex items-center gap-1">
                    {l.actor_type === 'ia' ? <Bot size={10} /> : <User size={10} />} {l.actor_name}
                  </p>
                )}

                {l.subject && <p className="text-sm text-white/90 mt-1 font-medium">{l.subject}</p>}
                {(l.summary || l.body) && (
                  <p className="text-sm text-white/60 mt-1 leading-relaxed whitespace-pre-wrap">
                    {l.summary || l.body}
                  </p>
                )}

                {hayMas && (
                  <button onClick={() => setAbierta(desplegada ? null : l.activity_id)}
                    className="mt-1.5 text-[11px] text-white/40 hover:text-white/70 flex items-center gap-1 transition">
                    {desplegada ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    {desplegada ? 'Ocultar' : l.transcript ? 'Ver la transcripción' : 'Ver más'}
                  </button>
                )}

                {desplegada && (
                  <div className="mt-2 p-3 bg-black/30 rounded-lg border border-white/5">
                    {l.media_url && (
                      <audio controls src={l.media_url} className="w-full mb-2 h-9">
                        Tu navegador no reproduce audio.
                      </audio>
                    )}
                    {l.transcript && (
                      <p className="text-xs text-white/50 leading-relaxed whitespace-pre-wrap max-h-56 overflow-y-auto">
                        {l.transcript}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
